"use client";

import { useEffect, useState, useRef } from "react";
import { ProAuthProvider, useProAuth } from "@/lib/pro/auth";
import {
  TenantProvider,
  TenantContext,
  BrandingConfig,
} from "@/lib/tenant/context";

/**
 * Inner component that has access to auth context.
 * When the user is logged in and has a retailerId, fetches retailer branding
 * and overrides the server-provided tenant defaults.
 */
function BrandingResolver({
  children,
  serverTenant,
}: {
  children: React.ReactNode;
  serverTenant: TenantContext;
}) {
  const { user, isLoading } = useProAuth();
  const [resolvedTenant, setResolvedTenant] =
    useState<TenantContext>(serverTenant);
  const fetchedForRef = useRef<string | null>(null);

  useEffect(() => {
    // If the server layout already resolved branding (via custom domain or ?retailer_id=), keep it
    if (serverTenant.retailerId) {
      setResolvedTenant(serverTenant);
      return;
    }

    // Wait for auth to finish loading
    if (isLoading) return;

    // No user or no retailer ID — stick with defaults
    if (!user?.retailerId) return;

    // Don't re-fetch if we already fetched for this retailer
    if (fetchedForRef.current === user.retailerId) return;

    fetchedForRef.current = user.retailerId;

    async function fetchBranding() {
      try {
        const res = await fetch(`/api/retailer/${user!.retailerId}`);
        if (!res.ok) {
          console.error(
            `[BrandingResolver] Failed to fetch retailer ${user!.retailerId}: ${res.status}`
          );
          return;
        }
        const retailer = await res.json();

        // Only a Pro retailer brands the Pro portal. Admins (and any user whose
        // retailer isn't Pro) can still sign in — see the login route — but the
        // portal must stay "Plantista Pro" rather than wearing a garden
        // center's identity. Mirrors the login check: only an explicit
        // `pro === false` opts out, so an unreadable flag never strips a
        // paying customer's branding. (`/api/retailer/[id]` requests
        // `?designer=true`, which is the only response that carries `pro`.)
        if (retailer.pro === false) {
          console.info(
            `[BrandingResolver] Retailer ${user!.retailerId} is not Pro — keeping default Plantista Pro branding`
          );
          return;
        }

        const brandingConfig = (retailer.brandingConfig ||
          retailer.branding_config ||
          {}) as any;
        const branding: BrandingConfig = {
          companyName:
            retailer.businessName ||
            retailer.business_name ||
            user!.retailerName ||
            "Plantista Pro",
          logo:
            brandingConfig.logo_url ||
            retailer.logoUrl ||
            retailer.logo_url ||
            "/logo512.png",
          primaryColor: brandingConfig.color_palette?.primary || "#10b981",
          secondaryColor: brandingConfig.color_palette?.secondary || "#059669",
          tertiaryColor: brandingConfig.color_palette?.tertiary || "#f0fdf4",
        };

        setResolvedTenant({
          ...serverTenant,
          retailerId: user!.retailerId,
          branding,
        });
      } catch (err) {
        console.error("[BrandingResolver] Error fetching branding:", err);
      }
    }

    fetchBranding();
  }, [user, isLoading, serverTenant.retailerId]);

  return <TenantProvider tenant={resolvedTenant}>{children}</TenantProvider>;
}

export function ProClientLayout({
  children,
  tenant,
}: {
  children: React.ReactNode;
  tenant: TenantContext;
}) {
  return (
    <ProAuthProvider>
      <BrandingResolver serverTenant={tenant}>{children}</BrandingResolver>
    </ProAuthProvider>
  );
}
